import { useCallback, useEffect, useState } from 'react';
import type { LeadRecord, LeadStatus } from '@360t/shared';
import { api } from '../lib/api';
import { LeadDrawer } from '../components/LeadDrawer';
import { OpportunityBadge } from '../components/OpportunityBadge';

const COLUMNS: { status: LeadStatus; label: string; hint: string }[] = [
  { status: 'new', label: 'New', hint: 'Found, not yet contacted' },
  { status: 'contacted', label: 'Contacted', hint: 'Outreach logged' },
  { status: 'replied', label: 'Replied', hint: 'They came back to us' },
  { status: 'won', label: 'Won', hint: 'Signed' },
  { status: 'lost', label: 'Lost', hint: 'Not now' },
];

/** Module 7 — pipeline board. Drag a card between columns to move the lead. */
export function PipelinePage() {
  const [leads, setLeads] = useState<LeadRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<LeadStatus | null>(null);
  const [openLead, setOpenLead] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // 200 is the API cap; a board beyond that wants filtering, not scrolling.
      const res = await api.listLeads({ take: 200, sort: 'opportunity' });
      setLeads(res.leads);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const move = async (id: string, status: LeadStatus) => {
    const previous = leads;
    // Optimistic: the board should feel instant, and we can roll back.
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.updateLead(id, { status });
    } catch {
      setLeads(previous);
    }
  };

  return (
    <div className="mx-auto max-w-[1600px] px-6 py-8">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-ink">Pipeline</h2>
          <p className="text-sm text-slate-500">Drag a lead between columns to move it along.</p>
        </div>
        <span className="text-xs text-slate-400">{loading ? 'Loading…' : `${leads.length} leads`}</span>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {COLUMNS.map((col) => {
          const items = leads.filter((l) => l.status === col.status);
          return (
            <div
              key={col.status}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(col.status);
              }}
              onDragLeave={() => setOver((s) => (s === col.status ? null : s))}
              onDrop={() => {
                if (dragging) void move(dragging, col.status);
                setDragging(null);
                setOver(null);
              }}
              className={`rounded-xl border p-3 transition ${
                over === col.status ? 'border-brand bg-brand/5' : 'border-line bg-white'
              }`}
            >
              <div className="mb-3 flex items-baseline justify-between">
                <div>
                  <h3 className="text-sm font-bold text-ink">{col.label}</h3>
                  <p className="text-[11px] text-slate-400">{col.hint}</p>
                </div>
                <span className="rounded-full bg-canvas px-2 py-0.5 text-xs font-bold text-slate-500">
                  {items.length}
                </span>
              </div>

              <div className="space-y-2">
                {items.map((lead) => (
                  <article
                    key={lead.id}
                    draggable
                    onDragStart={() => setDragging(lead.id)}
                    onDragEnd={() => setDragging(null)}
                    onClick={() => setOpenLead(lead.id)}
                    className={`cursor-grab rounded-lg border border-line bg-white p-3 shadow-sm transition hover:border-brand active:cursor-grabbing ${
                      dragging === lead.id ? 'opacity-40' : ''
                    } ${lead.doNotContact ? 'opacity-50' : ''}`}
                  >
                    <div className="mb-1.5 flex items-start justify-between gap-2">
                      <span className="text-sm font-semibold leading-tight text-ink">{lead.businessName}</span>
                      <OpportunityBadge score={lead.opportunityScore} />
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {[lead.city, lead.platform].filter(Boolean).join(' · ') || '—'}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {!lead.hasWebsite && (
                        <span className="rounded bg-poor/10 px-1.5 py-0.5 text-[10px] font-bold text-poor">NO SITE</span>
                      )}
                      {lead.websiteBroken && (
                        <span className="rounded bg-poor/10 px-1.5 py-0.5 text-[10px] font-bold text-poor">SITE DOWN</span>
                      )}
                      {lead.email && (
                        <span className="rounded bg-good/10 px-1.5 py-0.5 text-[10px] font-bold text-good">EMAIL</span>
                      )}
                      {lead.latestAudit?.overallScore !== undefined && lead.latestAudit?.overallScore !== null && (
                        <span className="rounded bg-canvas px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
                          AUDIT {lead.latestAudit.overallScore}
                        </span>
                      )}
                    </div>
                  </article>
                ))}
                {items.length === 0 && (
                  <p className="rounded-lg border border-dashed border-line py-6 text-center text-xs text-slate-300">
                    Drop here
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {openLead && (
        <LeadDrawer leadId={openLead} onClose={() => setOpenLead(null)} onChanged={() => void load()} />
      )}
    </div>
  );
}
